import { Injectable, signal } from '@angular/core';
import type { CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';

/**
 * Tracks every repeat-accordion drop list inside a form-builder `cdkDropListGroup` so each
 * list can declare an explicit deepest-first `cdkDropListConnectedTo` order.
 *
 * The CDK resolves the receiving container with `siblings.find(...)` in registration order.
 * The form builder nests field lists inside expansion panels of outer lists, so the outermost
 * (first-registered) list always contains the pointer too and would win every hit-test —
 * cross-level drops could never land in a nested list without this explicit ordering.
 */
@Injectable({ providedIn: 'root' })
export class FormBuilderDropListRegistry {
  readonly #listsByGroup = new Map<CdkDropListGroup<CdkDropList>, Map<CdkDropList, number>>();
  readonly #version = signal(0);

  register(group: CdkDropListGroup<CdkDropList>, dropList: CdkDropList): void {
    let lists = this.#listsByGroup.get(group);
    if (!lists) {
      lists = new Map();
      this.#listsByGroup.set(group, lists);
    }
    // DOM depth is fixed for the lifetime of the list element; deeper = more specific target.
    lists.set(dropList, this.#domDepth(dropList.element.nativeElement));
    this.#version.update(version => version + 1);
  }

  unregister(group: CdkDropListGroup<CdkDropList>, dropList: CdkDropList): void {
    const lists = this.#listsByGroup.get(group);
    if (!lists?.delete(dropList)) {
      return;
    }
    if (lists.size === 0) {
      this.#listsByGroup.delete(group);
    }
    this.#version.update(version => version + 1);
  }

  /** All drop lists in the group, deepest DOM node first. Reactive: reads an internal version signal. */
  connectedLists(group: CdkDropListGroup<CdkDropList>): CdkDropList[] {
    this.#version();
    const lists = this.#listsByGroup.get(group);
    if (!lists) {
      return [];
    }
    return [...lists.entries()].sort((a, b) => b[1] - a[1]).map(([list]) => list);
  }

  #domDepth(element: Element): number {
    let depth = 0;
    for (let node = element.parentElement; node; node = node.parentElement) {
      depth++;
    }
    return depth;
  }
}
